import { Button, Tabs, VFlow } from 'bold-ui'
import { ChartMonthSelector, DateRange } from 'bold-ui/lib/components/Chart'
import { PageLoading } from 'components/loading'
import { PecSwitch, PrivateRoute, TabLink } from 'components/route'
import { endOfMonth, startOfDay, startOfMonth } from 'date-fns'
import { useAcompanhamentoPreNatalCidadaoQuery, useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { orderBy } from 'lodash'
import React, { useState } from 'react'
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { GraficoAlturaUterinaView } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/altura-uterina/GraficoAlturaUterinaView'
import { GraficoGanhoPesoView } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/ganho-peso/GraficoGanhoPesoView'
import { GraficoPressaoArterial } from 'view/atendimentos/detail/components/modals/medicoes/graficos/sinais-vitais/GraficoPressaoArterial'
import { MedicoesPreNatalTable } from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/MedicoesPreNatalTable'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatalSorted,
  getMedicoesPeso10SemanasAposInicioGestacao,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'
import { getMedicoesComSinaisVitais } from 'view/atendimentos/detail/components/modals/medicoes/util-medicoes'
import { grupoCboVisualizarAcompanhamentos } from 'view/cidadao/acessos'

import { ExternalPaths } from '../model-acompanhamentoPreNatal'
import { getMedicoesPreNatal } from '../util-acompanhamentoPreNatal'

const GANHO_DE_PESO_URL = 'ganho-de-peso'
const ALTURA_UTERINA_URL = 'altura-uterina'
const PRESSAO_ARTERIAL_URL = 'pressao-arterial'

interface MedicoesGestacaoTabContentProps {
  cidadaoId: ID
  prontuarioId: ID
  dataInicioGestacao: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  isDataInicioGestacaoEcografica: boolean
  loading?: boolean
  externalPaths: ExternalPaths
  isVisualizacaoProntuario: boolean
}

export const MedicoesGestacaoTabContent = (props: MedicoesGestacaoTabContentProps) => {
  const {
    cidadaoId,
    prontuarioId,
    dataInicioGestacao,
    atendimentosProfUltimaGestacao,
    isDataInicioGestacaoEcografica,
    loading,
    externalPaths,
    isVisualizacaoProntuario,
  } = props

  const match = useRouteMatch()
  const history = useHistory()
  const location = useLocation()

  const ganhoDePesoPath = `${match.url}/${GANHO_DE_PESO_URL}${location.search}`
  const alturaUterinaPath = `${match.url}/${ALTURA_UTERINA_URL}${location.search}`
  const pressaoArterialPath = `${match.url}/${PRESSAO_ARTERIAL_URL}${location.search}`
  const isPressaoArterialTab = location.pathname.endsWith(pressaoArterialPath)

  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()
  const [dateRange, setDateRange] = useState<DateRange>({
    init: startOfMonth(now),
    end: startOfDay(endOfMonth(now)),
  })

  const {
    data: { cidadao: { prontuario = null } = {} },
    loading: loadingCidadao,
  } = useAcompanhamentoPreNatalCidadaoQuery({ variables: { id: cidadaoId }, fetchPolicy: 'cache-first' })

  const {
    data: { medicoes },
    loading: loadingMedicoes,
  } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    fetchPolicy: 'cache-first',
  })

  const medicoesAntropometricasPreNatal = getMedicoesAntropometricasPreNatalSorted(medicoes)
  const medicoesPreNatal = getMedicoesPreNatal(atendimentosProfUltimaGestacao)
  const medicoesComSinaisVitais = getMedicoesComSinaisVitais(medicoes)
  const medicaoForImcReferencia = getMedicaoForImcReferencia(dataInicioGestacao, medicoesAntropometricasPreNatal)
  const medicoesPeso10SemanasAposInicioGestacao = getMedicoesPeso10SemanasAposInicioGestacao(
    dataInicioGestacao,
    medicoesAntropometricasPreNatal
  )

  const renderGanhoDePeso = () => {
    if (loading || loadingMedicoes) return <PageLoading message='Carregando dados' />

    const hasMedicaoForImcReferencia =
      !isUndefinedOrNull(medicaoForImcReferencia?.peso) && !isUndefinedOrNull(medicaoForImcReferencia?.altura)

    return (
      <VFlow>
        <GraficoGanhoPesoView
          medicaoForImcReferencia={medicaoForImcReferencia}
          medicoesPeso10SemanasAposInicioGestacao={medicoesPeso10SemanasAposInicioGestacao}
          dataInicioGestacao={dataInicioGestacao}
        />
        {!isVisualizacaoProntuario && !hasMedicaoForImcReferencia && (
          <Button
            skin='outline'
            kind='primary'
            size='small'
            onClick={() => history.push(externalPaths.antropometriaPath)}
          >
            Acessar antropometria
          </Button>
        )}
      </VFlow>
    )
  }

  const renderAlturaUterina = () =>
    loading || loadingMedicoes ? (
      <PageLoading message='Carregando dados' />
    ) : (
      <GraficoAlturaUterinaView medicoesPreNatal={medicoesPreNatal} dataInicioGestacao={dataInicioGestacao} />
    )

  const renderPressaoArterial = () =>
    loadingMedicoes || loadingCidadao ? (
      <PageLoading message='Carregando dados' />
    ) : (
      <GraficoPressaoArterial
        medicoes={medicoesComSinaisVitais}
        gestacoes={prontuario?.gestacoes}
        dateRange={dateRange}
      />
    )

  return (
    <VFlow>
      <MedicoesPreNatalTable
        dataInicioGestacao={dataInicioGestacao}
        medicoesAntropometricasPreNatal={medicoesAntropometricasPreNatal}
        isIgEcografica={isDataInicioGestacaoEcografica}
        items={orderBy(medicoesPreNatal, 'numeroConsulta', 'desc')}
        loading={loading || loadingMedicoes}
        showPressaoArterial
      />
      <Tabs>
        <TabLink to={ganhoDePesoPath}>Ganho de peso</TabLink>
        <TabLink to={alturaUterinaPath}>Altura uterina</TabLink>
        <TabLink to={pressaoArterialPath}>Pressão arterial</TabLink>
        {isPressaoArterialTab && <ChartMonthSelector onChange={setDateRange} />}
      </Tabs>
      <PecSwitch>
        <Redirect exact from={match.url} to={ganhoDePesoPath} />
        <PrivateRoute
          path={`${match.url}/${GANHO_DE_PESO_URL}`}
          render={renderGanhoDePeso}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
        <PrivateRoute
          path={`${match.url}/${ALTURA_UTERINA_URL}`}
          render={renderAlturaUterina}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
        <PrivateRoute
          path={`${match.url}/${PRESSAO_ARTERIAL_URL}`}
          render={renderPressaoArterial}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
      </PecSwitch>
    </VFlow>
  )
}
